import type { ApiError as ApiErrorType } from '@/types'
import { getLocale } from '@/i18n'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

export type RequestParams = Record<string, string | number | boolean | undefined | null>

interface RequestOptions extends Omit<RequestInit, 'body'> {
  params?: RequestParams
  body?: unknown
}

export class ApiError extends Error {
  status: string
  error: string
  httpCode: number

  constructor(data: ApiErrorType) {
    super(data.message)
    this.name = 'ApiError'
    this.status = data.status
    this.error = data.error
    this.httpCode = data.http_code
  }
}

export function isApiError(error: unknown): error is ApiError {
  if (error instanceof ApiError) {
    return true
  }
  if (
    error !== null &&
    typeof error === 'object' &&
    'name' in error &&
    error.name === 'ApiError' &&
    'httpCode' in error
  ) {
    return true
  }
  return false
}

function getToken(): string | null {
  return localStorage.getItem('auth_token')
}

function clearAuth(): void {
  localStorage.removeItem('auth_token')
  localStorage.removeItem('auth_user')
}

// 401 from these endpoints means wrong credentials, not an expired session,
// so the error must reach the caller instead of triggering auto-logout
function isCredentialsEndpoint(endpoint: string): boolean {
  return endpoint.startsWith('/auth/') || endpoint === '/me/password'
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { params, body, ...fetchOptions } = options

  let url = `${API_BASE_URL}${endpoint}`
  if (params) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value))
      }
    })
    const queryString = searchParams.toString()
    if (queryString) {
      url += `?${queryString}`
    }
  }

  const headers = new Headers(fetchOptions.headers)
  headers.set('Accept-Language', getLocale())

  if (body && !(body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  const token = getToken()
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
    body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
  })

  if (response.status === 401 && !isCredentialsEndpoint(endpoint)) {
    clearAuth()
    window.location.href = '/dashboard/auth/login'
    throw new ApiError({
      status: 'error',
      error: 'unauthorized',
      message: 'Session expired. Please log in again.',
      http_code: 401,
    })
  }

  if (response.status === 204) {
    return undefined as T
  }

  const data = await response.json()

  if (!response.ok) {
    throw new ApiError(data)
  }

  return data
}

async function requestBlob(endpoint: string): Promise<{ blob: Blob; filename: string | null }> {
  const headers = new Headers()
  headers.set('Accept-Language', getLocale())

  const token = getToken()
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, { method: 'GET', headers })

  if (response.status === 401) {
    clearAuth()
    window.location.href = '/dashboard/auth/login'
    throw new ApiError({
      status: 'error',
      error: 'unauthorized',
      message: 'Session expired. Please log in again.',
      http_code: 401,
    })
  }

  if (!response.ok) {
    throw new ApiError(await response.json())
  }

  const disposition = response.headers.get('Content-Disposition')
  const filename = disposition?.match(/filename="?([^";]+)"?/)?.[1] ?? null

  return { blob: await response.blob(), filename }
}

export const api = {
  get<T>(endpoint: string, params?: RequestParams): Promise<T> {
    return request<T>(endpoint, { method: 'GET', params })
  },

  getBlob(endpoint: string): Promise<{ blob: Blob; filename: string | null }> {
    return requestBlob(endpoint)
  },

  getWithHeaders<T>(
    endpoint: string,
    params?: RequestParams,
    customHeaders?: Record<string, string>
  ): Promise<T> {
    return request<T>(endpoint, { method: 'GET', params, headers: customHeaders })
  },

  post<T>(endpoint: string, body?: unknown): Promise<T> {
    return request<T>(endpoint, { method: 'POST', body })
  },

  put<T>(endpoint: string, body?: unknown): Promise<T> {
    return request<T>(endpoint, { method: 'PUT', body })
  },

  delete<T>(endpoint: string): Promise<T> {
    return request<T>(endpoint, { method: 'DELETE' })
  },
}
